import React, { useMemo } from "react";

function SkeletonBlock({ className }) {
  return <div className={`animate-pulse bg-gray-900 rounded border border-gray-800 ${className}`} />;
}

const BAR_PX = 120;

/**
 * RD-0728 — stacked daily AWS vs margin (EUR) + inventory table; progressive skeletons.
 */
export function DeployBillingCostTrend({ usage, loading }) {
  const trend = useMemo(
    () => (usage && Array.isArray(usage.dailyInfrastructureTrend) ? usage.dailyInfrastructureTrend : []),
    [usage]
  );
  const inv = useMemo(
    () => (usage && Array.isArray(usage.inventoryResources) ? usage.inventoryResources : []),
    [usage]
  );

  const maxStack = useMemo(() => {
    let m = 0.01;
    for (const d of trend) {
      const aws = Number(d.awsEstimatedEur) || 0;
      const margin = Number(d.marginOnAwsEur) || 0;
      m = Math.max(m, aws + margin);
    }
    return m;
  }, [trend]);

  if (loading) {
    return (
      <section className="bg-off-black border border-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-medium mb-2">Usage &amp; cost trend (EUR)</h2>
        <SkeletonBlock className="h-40 w-full mb-4" />
        <SkeletonBlock className="h-32 w-full" />
      </section>
    );
  }

  if (!usage || !usage.success) {
    return (
      <section className="bg-off-black border border-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-medium mb-2">Usage &amp; cost trend (EUR)</h2>
        <p className="text-silver-medium text-sm">Could not load usage breakdown.</p>
      </section>
    );
  }

  return (
    <section
      className="bg-off-black border border-gray-800 rounded-lg p-6"
      aria-labelledby="deploy-cost-trend-heading"
    >
      <h2 id="deploy-cost-trend-heading" className="text-xl font-medium mb-2">
        Usage &amp; cost trend (EUR)
      </h2>
      <p className="text-silver-medium text-sm mb-4">
        Each day is a uniform slice of month-to-date AWS estimate and margin on AWS (when Cost Explorer provides
        confirmed + billed amounts). MCP billable actions are shown under the day when &gt; 0.
      </p>

      {trend.length === 0 ? (
        <p className="text-silver-medium text-sm mb-6">No days in range for this month (UTC).</p>
      ) : (
        <div className="overflow-x-auto mb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div
            className="flex items-end gap-0.5 sm:gap-1 min-w-max"
            style={{ height: `${BAR_PX + 24}px` }}
            role="img"
            aria-label="Stacked daily AWS estimate and margin in EUR"
          >
            {trend.map((d) => {
              const aws = Number(d.awsEstimatedEur) || 0;
              const margin = Number(d.marginOnAwsEur) || 0;
              const total = aws + margin;
              const billable = Number(d.billableActions) || 0;
              const barH = maxStack > 0 ? (total / maxStack) * BAR_PX : 0;
              return (
                <div key={d.day} className="flex flex-col items-center w-6 sm:w-8 flex-shrink-0">
                  <div
                    className="flex flex-col justify-end w-full rounded-t overflow-hidden bg-gray-950/80"
                    style={{ height: `${BAR_PX}px` }}
                  >
                    <div style={{ height: `${BAR_PX - barH}px` }} aria-hidden />
                    <div
                      className="flex flex-col justify-end w-full"
                      style={{ height: `${barH}px`, minHeight: total > 0 ? 2 : 0 }}
                    >
                      {margin > 0 ? (
                        <div
                          className="bg-blue-900/80 w-full"
                          style={{ flex: margin, minHeight: margin > 0 ? 1 : 0 }}
                          title={`Margin on AWS €${margin.toFixed(2)}`}
                        />
                      ) : null}
                      {aws > 0 ? (
                        <div
                          className="bg-slate-600 w-full"
                          style={{ flex: aws, minHeight: aws > 0 ? 1 : 0 }}
                          title={`AWS estimate €${aws.toFixed(2)}`}
                        />
                      ) : null}
                    </div>
                  </div>
                  <span className="text-[9px] sm:text-[10px] text-silver-medium mt-1 text-center leading-tight">
                    {d.day.slice(8)}
                  </span>
                  <span
                    className="text-[9px] text-silver-medium/80 min-h-[14px] inline-block"
                    title="Billable MCP actions (UTC day)"
                  >
                    {billable > 0 ? billable : ""}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-silver-medium mt-2">
            <span>
              <span className="inline-block w-2 h-2 bg-slate-600 rounded mr-1 align-middle" /> AWS estimate
            </span>
            <span>
              <span className="inline-block w-2 h-2 bg-blue-900/80 rounded mr-1 align-middle" /> Margin on AWS
            </span>
          </div>
        </div>
      )}

      <h3 className="text-lg font-medium mb-2">Per-resource (active inventory)</h3>
      {inv.length === 0 ? (
        <p className="text-silver-medium text-sm">
          No EC2 inventory rows yet — infrastructure estimates use the flat per-instance model until inventory syncs.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-800 text-silver-medium">
                <th className="py-2 pr-3">Service</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Region</th>
                <th className="py-2 pr-3">Resource</th>
                <th className="py-2 pr-3 text-right">MTD (EUR)</th>
                <th className="py-2 text-right">Projected (EUR)</th>
              </tr>
            </thead>
            <tbody>
              {inv.map((r) => (
                <tr key={`${r.service}-${r.awsResourceId}`} className="border-b border-gray-900">
                  <td className="py-2 pr-3">{r.service}</td>
                  <td className="py-2 pr-3">{r.instanceType || "—"}</td>
                  <td className="py-2 pr-3">{r.region || "—"}</td>
                  <td className="py-2 pr-3 break-all max-w-[10rem]">{r.awsResourceId}</td>
                  <td className="py-2 pr-3 text-right">€{Number(r.estimatedMtdEur).toFixed(2)}</td>
                  <td className="py-2 text-right">€{Number(r.projectedFullMonthEur).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
